import { createRef, lazy, createContext, ReactElement, useState } from "react";
import { Routes, Route, Navigate, BrowserRouter } from "react-router-dom";
import { SnackbarProvider } from "notistack";

// Utils
import { getEffectiveRegistrationStep } from "./utils/registration";

// Styles
import "./scss/App.scss";
import themes from "./themes";

// Config && settings
import settings from "./settings.json";
import { isLocalApp } from "./config/appEnv";

// Mui
import { ThemeProvider } from "@mui/material/styles";
import { CssBaseline, StyledEngineProvider } from "@mui/material";

// Hooks
import { useFetchCoach } from "./hooks/useFetchCoach";
import { useFetchSubscriptionPlans } from "./hooks/useFetchSubscriptionPlans";

// Custom components
import NavigationScroll from "./components/layout/NavigationScroll";
import Loadable from "./components/general/Loadable";
import Loader from "./components/general/Loader";
import MetaTracker from "./components/general/MetaTracker";
import { OnboardingProvider } from "./onboarding/OnboardingProvider";

// Pages
const Settings = Loadable(lazy(() => import("./components/pages/settings/Settings")));
const MainLayout = Loadable(lazy(() => import("./components/layout/MainLayout")));
const MinimalLayout = Loadable(lazy(() => import("./components/layout/MinimalLayout")));
const Login = Loadable(lazy(() => import("./components/pages/auth/Login")));
const User = Loadable(lazy(() => import("./components/pages/user/User")));
const PendingClientDetail = Loadable(lazy(() => import("./components/pages/pendingClient/PendingClientDetail")));
const Users = Loadable(lazy(() => import("./components/pages/users/Users")));
const Account = Loadable(lazy(() => import("./components/pages/account/AccountPage")));
const ChangeSubscription = Loadable(lazy(() => import("./components/pages/account/change-subscription/ChangeSubscriptionPage")));
const Tutorials = Loadable(lazy(() => import("./components/pages/tutorials/Tutorials")));
const LocalAdminAccess = Loadable(lazy(() => import("./components/pages/local/AdminAccess")));
const LocalFeatureFlags = Loadable(lazy(() => import("./components/pages/local/FeatureFlags")));
const LocalMigrations = Loadable(lazy(() => import("./components/pages/local/Migrations")));
const LocalDeleteUsersByPhoneNumber = Loadable(lazy(() => import("./components/pages/local/DeleteUsersByPhoneNumber")));
const LocalDeleteCoachesByEmail = Loadable(lazy(() => import("./components/pages/local/DeleteCoachesByEmail")));
const LocalDeletePendingClientInvites = Loadable(lazy(() => import("./components/pages/local/DeletePendingClientInvites")));
const LocalCoachDataLookup = Loadable(lazy(() => import("./components/pages/local/CoachDataLookup")));
const LocalGhl = Loadable(lazy(() => import("./components/pages/local/Ghl")));
const LocalNotificationTesting = Loadable(lazy(() => import("./components/pages/local/NotificationTesting")));
const Register = Loadable(lazy(() => import("./components/pages/auth/Register/Register")));
const ForgotPassword = Loadable(lazy(() => import("./components/pages/auth/ForgotPassword")));
const ChangePassword = Loadable(lazy(() => import("./components/pages/auth/ChangePassword")));
const JoinRedirect = Loadable(lazy(() => import("./components/pages/auth/JoinRedirect")));
const DeleteAccountRequest = Loadable(lazy(() => import("./components/pages/DeleteAccountRequest")));

interface RouteWrapperProps {
    title: string;
    children: ReactElement;
}

const PrivateRoute = ({ title, children }: RouteWrapperProps): JSX.Element => {
    const debug = false;
    const { currentCoach, authUser } = useFetchCoach();

    document.title = `${title} - ${settings.app.title}`;

    const authIsInitializing = authUser === undefined;
    const coachIsInitializing = Boolean(authUser) && currentCoach === undefined;

    if (authIsInitializing || coachIsInitializing) {
        if (debug) {
            console.log("[PrivateRoute] waiting for guard inputs", {
                authUser,
                currentCoach,
                authIsInitializing,
                coachIsInitializing,
                pathname: location.pathname,
            });
        }

        return <Loader />;
    }

    if (authUser === null) {
        return <Navigate to="/register" state={{ from: location.pathname }} replace />;
    }

    const accountStatus = currentCoach?.status;
    const subStatus = currentCoach?.subscription?.status;
    const hasPremiumAccess = currentCoach?.access?.premium;
    const isGrandfathered = currentCoach?.entitlements?.grandfathered === true;
    const registrationStep = getEffectiveRegistrationStep(currentCoach);
    const permittedSubscriptionStatuses = ["active", "trialing", "in_grace_period"];

    const registrationIsComplete = registrationStep === "complete";
    const accountIsActive = currentCoach?.status === "active";
    const subscriptionHasAccess = Boolean(subStatus && permittedSubscriptionStatuses.includes(subStatus));
    const hasBillingAccess = Boolean(hasPremiumAccess || isGrandfathered || subscriptionHasAccess);

    if (debug) {
        console.log(" ------------> Coach Info <------------ ");
        console.log("%csubStatus --->", "color:orange", subStatus);

        console.log("%cregistrationIsComplete --->", "color:orange", registrationIsComplete);
        console.log("%caccountIsActive --->", "color:orange", accountIsActive);
        console.log("%csubscriptionHasAccess --->", "color:orange", subscriptionHasAccess);
        console.log("%chasBillingAccess --->", "color:orange", hasBillingAccess);

        console.log("%chasPremiumAccess --->", "color:orange", hasPremiumAccess);
        console.log("%cisGrandfathered --->", "color:orange", isGrandfathered);
        console.log("%cregistrationStep --->", "color:orange", registrationStep);
        console.log("%caccountStatus --->", "color:orange", accountStatus);
        console.log(" ------------> END <------------ ");
        console.log("");
    }

    if (registrationStep && registrationStep !== "complete" && location.pathname !== "/register") {
        if (debug) console.log("1");
        return <Navigate to="/register" replace />;
    }

    if (registrationIsComplete && accountIsActive && hasBillingAccess && location.pathname === "/register") {
        if (debug) console.log("2b");
        return <Navigate to="/" replace />;
    }

    if (registrationIsComplete && (!accountIsActive || !hasBillingAccess) && location.pathname !== "/account") {
        if (debug) console.log("4");
        return <Navigate to="/account" replace />;
    }

    return children;
};

const PublicRoute = ({ title, children }: RouteWrapperProps): JSX.Element => {
    document.title = `${title} - ${settings.app.title}`;
    return children;
};

const noopSetClientName: (name: string) => void = () => undefined;

export const ClientContext = createContext({
    clientName: "",
    setClientName: noopSetClientName,
});

const App: React.FC = () => {
    //console.log(`%cVersion: ${versionObj && versionObj.version}`, "color: #bada55");

    const snackbarRef = createRef<unknown>();
    useFetchSubscriptionPlans();

    // States
    const [clientName, setClientName] = useState("");
    return (
        <ClientContext.Provider value={{ clientName, setClientName }}>
            <StyledEngineProvider injectFirst>
                <SnackbarProvider
                    className="snackbar__container"
                    ref={snackbarRef}
                    maxSnack={3}
                    anchorOrigin={{
                        vertical: "top",
                        horizontal: "right",
                    }}
                    autoHideDuration={2000}
                >
                    <ThemeProvider theme={themes()}>
                        <CssBaseline />
                        <NavigationScroll>
                            <BrowserRouter>
                                <MetaTracker />
                                <OnboardingProvider>
                                    <Routes>
                                        <Route
                                            path=""
                                            element={
                                                <PrivateRoute title="Clients">
                                                    <MainLayout pageName="Clients">
                                                        <Users />
                                                    </MainLayout>
                                                </PrivateRoute>
                                            }
                                        />

                                        <Route
                                            path="settings"
                                            element={
                                                <PrivateRoute title="Settings">
                                                    <MainLayout pageName="Settings">
                                                        <Settings />
                                                    </MainLayout>
                                                </PrivateRoute>
                                            }
                                        />

                                        <Route path="user">
                                            <Route path=":id">
                                                <Route
                                                    path=":index"
                                                    element={
                                                        <PrivateRoute title={clientName || "Client"}>
                                                        <MainLayout
                                                            pageName={clientName || "Client"}
                                                        >
                                                            <User />
                                                        </MainLayout>
                                                        </PrivateRoute>
                                                    }
                                                />
                                            </Route>
                                        </Route>
                                        <Route path="pending-client">
                                            <Route
                                                path=":pendingClientId"
                                                element={
                                                    <PrivateRoute title="Pending Client">
                                                        <MainLayout
                                                            pageName={"Pending Client"}
                                                            removeBackground={true}
                                                            contentClass="MainCard__nopadding"
                                                        >
                                                            <PendingClientDetail />
                                                        </MainLayout>
                                                    </PrivateRoute>
                                                }
                                            />
                                        </Route>
                                        <Route
                                            path="account"
                                            element={
                                                <PrivateRoute title="Account">
                                                    <MainLayout pageName="Account">
                                                        <Account />
                                                    </MainLayout>
                                                </PrivateRoute>
                                            }
                                        />
                                        <Route
                                            path="account/change-subscription"
                                            element={
                                                <PrivateRoute title="Change Subscription">
                                                    <MainLayout pageName="Change Subscription">
                                                        <ChangeSubscription />
                                                    </MainLayout>
                                                </PrivateRoute>
                                            }
                                        />
                                        <Route
                                            path="tutorials"
                                            element={
                                                <PrivateRoute title="Tutorials">
                                                    <MainLayout pageName="Tutorials">
                                                        <Tutorials />
                                                    </MainLayout>
                                                </PrivateRoute>
                                            }
                                        />
                                        {isLocalApp && (
                                            <Route
                                                path="local/coach-data-lookup"
                                                element={
                                                    <PrivateRoute title="Coach Data Lookup">
                                                        <MainLayout pageName="Coach Data Lookup">
                                                            <LocalCoachDataLookup />
                                                        </MainLayout>
                                                    </PrivateRoute>
                                                }
                                            />
                                        )}
                                        {isLocalApp && (
                                            <Route
                                                path="local/admin-access"
                                                element={
                                                    <PrivateRoute title="Admin Access">
                                                        <MainLayout pageName="Admin Access">
                                                            <LocalAdminAccess />
                                                        </MainLayout>
                                                    </PrivateRoute>
                                                }
                                            />
                                        )}
                                        {isLocalApp && (
                                            <Route
                                                path="local/migrations"
                                                element={
                                                    <PrivateRoute title="Local Migrations">
                                                        <MainLayout pageName="Migrations">
                                                            <LocalMigrations />
                                                        </MainLayout>
                                                    </PrivateRoute>
                                                }
                                            />
                                        )}
                                        {isLocalApp && (
                                            <Route
                                                path="local/feature-flags"
                                                element={
                                                    <PrivateRoute title="Local Feature Flags">
                                                        <MainLayout pageName="Local Feature Flags">
                                                            <LocalFeatureFlags />
                                                        </MainLayout>
                                                    </PrivateRoute>
                                                }
                                            />
                                        )}
                                        {isLocalApp && (
                                            <Route
                                                path="local/ghl"
                                                element={
                                                    <PrivateRoute title="GHL">
                                                        <MainLayout pageName="GHL">
                                                            <LocalGhl />
                                                        </MainLayout>
                                                    </PrivateRoute>
                                                }
                                            />
                                        )}
                                        {isLocalApp && (
                                            <Route
                                                path="local/notification-testing"
                                                element={
                                                    <PrivateRoute title="Notification Testing">
                                                        <MainLayout pageName="Notification Testing">
                                                            <LocalNotificationTesting />
                                                        </MainLayout>
                                                    </PrivateRoute>
                                                }
                                            />
                                        )}
                                        {isLocalApp && (
                                            <Route
                                                path="local/delete-users-by-phone-number"
                                                element={
                                                    <PrivateRoute title="User Data Operations">
                                                        <MainLayout pageName="User Data Operations">
                                                            <LocalDeleteUsersByPhoneNumber />
                                                        </MainLayout>
                                                    </PrivateRoute>
                                                }
                                            />
                                        )}
                                        {isLocalApp && (
                                            <Route
                                                path="local/delete-coaches-by-email"
                                                element={
                                                    <PrivateRoute title="Delete Coaches by Email">
                                                        <MainLayout pageName="Delete Coaches">
                                                            <LocalDeleteCoachesByEmail />
                                                        </MainLayout>
                                                    </PrivateRoute>
                                                }
                                            />
                                        )}
                                        {isLocalApp && (
                                            <Route
                                                path="local/delete-pending-client-invites"
                                                element={
                                                    <PrivateRoute title="Delete Pending Client Invites">
                                                        <MainLayout pageName="Delete Pending Client Invites">
                                                            <LocalDeletePendingClientInvites />
                                                        </MainLayout>
                                                    </PrivateRoute>
                                                }
                                            />
                                        )}

                                        <Route
                                            path="login"
                                            element={
                                                <PublicRoute title="Log In">
                                                    <MinimalLayout>
                                                        <Login />
                                                    </MinimalLayout>
                                                </PublicRoute>
                                            }
                                        />
                                        <Route
                                            path="register/success"
                                            element={
                                                <PrivateRoute title="Registration Success">
                                                    <MinimalLayout>
                                                        <Register />
                                                    </MinimalLayout>
                                                </PrivateRoute>
                                            }
                                        />
                                        <Route
                                            path="register"
                                            element={
                                                <PublicRoute title="Sign Up">
                                                    <MinimalLayout>
                                                        <Register />
                                                    </MinimalLayout>
                                                </PublicRoute>
                                            }
                                        />
                                        <Route
                                            path="join"
                                            element={
                                                <PublicRoute title="Join">
                                                    <MinimalLayout>
                                                        <JoinRedirect />
                                                    </MinimalLayout>
                                                </PublicRoute>
                                            }
                                        />
                                        <Route
                                            path="delete-account"
                                            element={
                                                <PublicRoute title="Account Deletion">
                                                    <DeleteAccountRequest />
                                                </PublicRoute>
                                            }
                                        />
                                        <Route
                                            path="/forgotPassword"
                                            element={
                                                <PublicRoute title="Forgot Password">
                                                    <ForgotPassword />
                                                </PublicRoute>
                                            }
                                        />
                                        <Route
                                            path="/changePassword"
                                            element={
                                                <PublicRoute title="Change Password">
                                                    <ChangePassword />
                                                </PublicRoute>
                                            }
                                        />

                                        <Route path="*" element={<Register />} />
                                    </Routes>
                                </OnboardingProvider>
                            </BrowserRouter>
                        </NavigationScroll>
                    </ThemeProvider>
                </SnackbarProvider>
            </StyledEngineProvider>
        </ClientContext.Provider>
    );
};

export default App;
